import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { usePermissions } from '@/hooks/useAuth'
import { ArrowRight, Check, RotateCcw, X } from 'lucide-react'
import {
  laboratoryTestLabel,
  parseLaboratoryTests,
} from '../../../convex/lib/laboratoryTests'
import {
  normalizeSkipPolicy,
  type SkipPolicy,
} from '../../../convex/lib/phasePolicies'

type Phase = { id: number; name: string; display_name?: string; skip_policy: SkipPolicy }

function isDeliveryPhase(name: string) {
  return name.trim().toLocaleLowerCase('it-IT') === 'consegna'
}

function normalizeTests(raw: unknown): Phase[] {
  return parseLaboratoryTests(raw).map((test) => {
      return {
        id: test.id,
        name: test.name,
        ...(test.display_name ? { display_name: test.display_name } : {}),
        skip_policy: normalizeSkipPolicy(test.skip_policy),
      }
    })
}

export function CompanyPhasesEditor({
  companyId,
  companyName,
  organizationId,
  organizationName,
  organizationScope = false,
  listinoId,
  laboratoryTests,
}: {
  companyId?: Id<'companies'>
  organizationId?: Id<'organizations'>
  organizationName?: string
  organizationScope?: boolean
  companyName?: string
  listinoId: Id<'listino'>
  laboratoryTests: unknown
}) {
  const { hasPermission } = usePermissions()
  const canManageCompanyPhases = hasPermission('manage_company_phases')
  const canManagePhasePolicies = hasPermission('manage_phase_policies')
  const canConfigure = canManageCompanyPhases || canManagePhasePolicies
  const catalog = useMemo(() => normalizeTests(laboratoryTests), [laboratoryTests])
  const clinicSaved = useQuery(api.companyPhases.getCompanyServicePhases,
    !organizationScope && companyId ? { company_id: companyId, listino_id: listinoId } : 'skip')
  const organizationSaved = useQuery(api.companyPhases.getOrganizationServicePhases,
    organizationId ? { listino_id: listinoId } : 'skip')
  const controlledByOrganization = !organizationScope && Boolean(organizationSaved)
  const canEdit = canConfigure && !controlledByOrganization
  const saved = organizationScope || controlledByOrganization ? organizationSaved : clinicSaved
  const loading = (organizationId && organizationSaved === undefined) ||
    (!organizationScope && companyId && clinicSaved === undefined)
  const scopeName = organizationScope || controlledByOrganization ? 'società' : 'clinica'
  const scopeLabel = organizationScope || controlledByOrganization ? organizationName : companyName
  const saveClinic = useMutation(api.companyPhases.saveCompanyServicePhases)
  const saveOrganization = useMutation(api.companyPhases.saveOrganizationServicePhases)

  const [active, setActive] = useState<Phase[]>([])
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [feedback, setFeedback] = useState<string | null>(null)

  useEffect(() => {
    if (dirty && !controlledByOrganization) return
    if (saved) {
      setActive(saved.phases)
    } else {
      setActive(catalog)
    }
  }, [saved, catalog, dirty, controlledByOrganization])

  if (!canConfigure) return null
  if (loading) return <p className="mt-6 text-sm text-gray-600" role="status">Caricamento configurazione prove…</p>
  if (catalog.length === 0) {
    return (
      <Card className="mt-4">
        <CardContent className="py-4 text-sm text-gray-600">
          Nessuna fase PrimoUp sul listino: sincronizza i test della prestazione prima di configurare.
        </CardContent>
      </Card>
    )
  }

  const activeIds = new Set(active.map((p) => p.id))
  const availableLeft = catalog.filter((p) => !activeIds.has(p.id))

  const removePhase = (id: number) => {
    const phase = active.find((item) => item.id === id)
    if (phase && isDeliveryPhase(phase.name)) {
      setFeedback('La fase “Consegna” è obbligatoria perché chiude il lavoro e abilita la contabilità.')
      return
    }
    setActive((prev) => prev.filter((p) => p.id !== id))
    setDirty(true)
    setFeedback(null)
  }

  const addPhase = (phase: Phase) => {
    setActive((prev) => {
      const catalogOrder = catalog.map((c) => c.id)
      const next = [...prev, phase]
      next.sort((a, b) => catalogOrder.indexOf(a.id) - catalogOrder.indexOf(b.id))
      return next
    })
    setDirty(true)
    setFeedback(null)
  }

  const resetToCatalog = () => {
    setActive(catalog)
    setDirty(true)
    setFeedback(null)
  }

  const handleSave = async () => {
    if (active.length === 0) {
      setFeedback('Lascia almeno una fase attiva.')
      return
    }
    setSaving(true)
    setFeedback(null)
    try {
      if (organizationScope) {
        await saveOrganization({ listino_id: listinoId, phases: active })
      } else if (companyId) {
        await saveClinic({ company_id: companyId, listino_id: listinoId, phases: active })
      } else {
        throw new Error('Seleziona la clinica per salvare le fasi.')
      }
      setDirty(false)
      setFeedback(organizationScope ? 'Fasi salvate per tutte le cliniche che usano questa voce societaria.' : 'Fasi salvate per questa clinica.')
    } catch (e) {
      setFeedback(e instanceof Error ? e.message : 'Errore salvataggio')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card className="mt-6 border-primary/20">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Fasi di lavoro per la {scopeName}{scopeLabel ? `: ${scopeLabel}` : ''}</CardTitle>
        {(organizationScope || controlledByOrganization) && (
          <p className="text-sm text-gray-600">
            Queste regole valgono per tutte le cliniche che usano questa voce di listino e prevalgono sulle configurazioni delle singole cliniche.
            {controlledByOrganization && ' Per modificarle, contatta il laboratorio.'}
            {organizationScope && !organizationSaved && ' Salva le fasi per attivare la configurazione societaria; fino ad allora restano valide le regole delle cliniche o del catalogo.'}
          </p>
        )}
        <p className="text-xs text-gray-600">
          La regola indica cosa accade quando la clinica salta quella fase per sceglierne una successiva.
          Le prove senza una regola specifica usano il salto libero.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <fieldset disabled={saving} className="min-w-0 space-y-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto_1fr]">
          <div className="rounded-md border bg-gray-50 p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              Catalogo PrimoUp
            </div>
            <ul className="space-y-1.5">
              {catalog.map((p) => (
                <li
                  key={p.id}
                  className="flex items-center justify-between gap-2 rounded border border-transparent bg-white px-2 py-1.5 text-sm"
                >
                  <span className="min-w-0">
                    <span className="block truncate">{laboratoryTestLabel(p)}</span>
                    <span className="block text-[10px] text-gray-500">
                      {p.display_name ? `PrimoUp: ${p.name} · ` : ''}ID {p.id}
                    </span>
                  </span>
                  {canEdit && canManageCompanyPhases && !activeIds.has(p.id) && (
                    <Button type="button" size="sm" variant="outline" onClick={() => addPhase(p)}>
                      Aggiungi
                    </Button>
                  )}
                </li>
              ))}
            </ul>
            {availableLeft.length === 0 && (
              <p className="mt-2 text-xs text-gray-500">Tutte le fasi sono già a destra.</p>
            )}
          </div>

          <div className="hidden items-center justify-center md:flex">
            <ArrowRight className="h-5 w-5 text-gray-400" />
          </div>

          <div className="rounded-md border border-primary/30 bg-white p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">
              Fasi attive {scopeName} (ordine Rx)
            </div>
            <ol className="space-y-1.5">
              {active.map((p, idx) => (
                <li
                  key={p.id}
                  className="flex items-center justify-between gap-2 rounded border px-2 py-1.5 text-sm"
                >
                  <div className="min-w-0 flex-1">
                    <div>
                      <span className="mr-2 text-xs font-semibold text-gray-400">{idx + 1}.</span>
                      {laboratoryTestLabel(p)}
                      {p.display_name && (
                        <span className="ml-2 text-[10px] text-gray-500">PrimoUp: {p.name}</span>
                      )}
                      {isDeliveryPhase(p.name) && (
                        <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-semibold text-green-800">
                          Obbligatoria
                        </span>
                      )}
                    </div>
                    {canEdit && canManagePhasePolicies ? (
                      <select
                        value={p.skip_policy}
                        onChange={(event) => {
                          const skip_policy = event.target.value as SkipPolicy
                          setActive((previous) =>
                            previous.map((phase) =>
                              phase.id === p.id ? { ...phase, skip_policy } : phase
                            )
                          )
                          setDirty(true)
                          setFeedback(null)
                        }}
                        className="mt-1 w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs"
                        aria-label={`Regola salto ${p.name}`}
                      >
                        <option value="free">Salto libero</option>
                        <option value="approval_required">Richiede approvazione laboratorio</option>
                        <option value="blocked">Non può essere saltata</option>
                      </select>
                    ) : (
                      <p className="mt-1 text-xs text-gray-500">
                        {p.skip_policy === 'free'
                          ? 'Salto libero'
                          : p.skip_policy === 'approval_required'
                            ? 'Richiede approvazione laboratorio'
                            : 'Non può essere saltata'}
                      </p>
                    )}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={
                      !canEdit ||
                      !canManageCompanyPhases ||
                      active.length <= 1 ||
                      isDeliveryPhase(p.name)
                    }
                    onClick={() => removePhase(p.id)}
                    aria-label={`Rimuovi ${laboratoryTestLabel(p)}`}
                    title={isDeliveryPhase(p.name) ? 'La fase Consegna non può essere rimossa' : undefined}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {canEdit && <div className="flex flex-wrap items-center gap-2">
          <Button type="button" onClick={() => void handleSave()} disabled={saving || (!dirty && Boolean(saved))}>
            <Check className="mr-1.5 h-4 w-4" />
            {saving ? 'Salvataggio…' : `Salva fasi ${scopeName}`}
          </Button>
          {canManageCompanyPhases && (
            <Button type="button" variant="outline" onClick={resetToCatalog}>
              <RotateCcw className="mr-1.5 h-4 w-4" />
              Ripristina catalogo
            </Button>
          )}
          {feedback && <span role="status" className="text-sm text-gray-700">{feedback}</span>}
        </div>}
        </fieldset>
      </CardContent>
    </Card>
  )
}
